import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import modalActions, {
  OpenMonthlySendsWarningModalParams,
} from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPageSelector,
  activeMessagesStatisticSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { defaultProPlanSelector } from '@mm/state/selectors/subscriptionSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { SEEN_SEND_LIMIT_NOTIFICATION_TABLE_KEY } from '@mm/constants/message-send-limits';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { LocalStorageService } from '@mm/utils';
import JSONUtils from '@mm/state/helpers/json-utils';
import { useFreeTrialUpgradeModal } from '../FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { ModalInstanceProps } from '../interfaces';
import SendLimitNoticeTemplate from '../templates/SendLimitNoticeTemplate/SendLimitNoticeTemplate';
import { getWarningText } from './helpers';
import { SendLimitLookupTable } from './interfaces';
import './MonthlySendsWarningModal.pcss';

type OuterProps = OpenMonthlySendsWarningModalParams;

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  ModalInstanceProps;

type Props = OuterProps & InnerProps;

const useSetSeenWarningLocalStorage = (props: Props) => {
  const { facebookPageOverride, activeFacebookPage } = props;
  const facebookPage = facebookPageOverride || activeFacebookPage;
  const messagesStatistic = facebookPageOverride
    ? facebookPageOverride.messagesStatistic
    : props.activeMessagesStatistic;

  useEffect(() => {
    if (facebookPage) {
      const sendLimitLookupTable = LocalStorageService.getItem(
        SEEN_SEND_LIMIT_NOTIFICATION_TABLE_KEY,
      );
      const parsedLookupTable =
        JSONUtils.safeParse<SendLimitLookupTable>(sendLimitLookupTable) || {};

      if (messagesStatistic.utilizationPercentage > 99) {
        parsedLookupTable.oneHundredPercent =
          parsedLookupTable.oneHundredPercent || {};
        parsedLookupTable.oneHundredPercent[facebookPage.id] = true;
      } else {
        parsedLookupTable.eightyToNinetyNinePercent =
          parsedLookupTable.eightyToNinetyNinePercent || {};
        parsedLookupTable.eightyToNinetyNinePercent[facebookPage.id] = true;
      }

      const stringifiedLookupTable =
        JSONUtils.toString(parsedLookupTable) || '';

      LocalStorageService.setItem(
        SEEN_SEND_LIMIT_NOTIFICATION_TABLE_KEY,
        stringifiedLookupTable,
      );
    }
  }, [facebookPage, messagesStatistic]);
};

const useOnUpgradeClick = (props: Props) => {
  const {
    facebookPageOverride,
    activeFacebookPage,
    defaultProPlan,
    openBillingModal,
    closeModal,
    sendOveragesEnabled,
  } = props;
  const facebookPage = facebookPageOverride || activeFacebookPage;
  const planInfo = getPlanInfo(facebookPage);
  const router = useRouter();

  return useCallback(async () => {
    if (planInfo.isFreePlan) {
      if (defaultProPlan && facebookPage) {
        openBillingModal(facebookPage, defaultProPlan);
      }

      closeModal();
    } else if (facebookPage) {
      const subscriptionId = facebookPage.subscription
        ? facebookPage.subscription.id
        : undefined;

      if (subscriptionId) {
        router.history.push('/settings/billing');
        /*const skipFetchActivePage = !!facebookPageOverride;
       
        await sendOveragesEnabled(
          subscriptionId,
          true,
          facebookPage.id,
          skipFetchActivePage,
        );*/
        closeModal();
      }
    }
  }, [
    planInfo,
    closeModal,
    defaultProPlan,
    facebookPage,
    sendOveragesEnabled,
    openBillingModal,
    facebookPageOverride,
  ]);
};

const MonthlySendsWarningModal = (props: Props) => {
  const {
    closeModal,
    activeFacebookPage,
    activeMessagesStatistic,
    facebookPageOverride,
  } = props;
  const facebookPage = facebookPageOverride || activeFacebookPage;
  const messagesStatistic = facebookPageOverride
    ? facebookPageOverride.messagesStatistic
    : activeMessagesStatistic;

  const { isFreeTrialPromotionWithFutureSubscription } =
    useFreeTrialUpgradeModal(facebookPage);
  const warningText = getWarningText(messagesStatistic, facebookPage);
  const planInfo = getPlanInfo(facebookPage);
  const onUpgradeClick = useOnUpgradeClick(props);
  const upgradeButtonCaption = planInfo.isFreePlan ? 'Upgrade' : 'Upgrade Now';

  // Handle the edge case where the notification fires for Legacy Plans (race condition)
  if (
    (planInfo.isLegacyPlan && !planInfo.isPromoPlan) ||
    isFreeTrialPromotionWithFutureSubscription
  ) {
    closeModal();
  }

  const noticeText = (
    <>
      {!planInfo.isAnyTeamPlan && (
        <span className="monthly-sends-warning-modal__page-name">
          {facebookPage ? facebookPage.name : ''}
        </span>
      )}

      {warningText}
    </>
  );

  useSetSeenWarningLocalStorage(props);

  if (!facebookPage) {
    return null;
  }

  if (messagesStatistic.count <= 0) {
    return null;
  }

  const templateProps = {
    noticeText,
    closeModal,
    activeMessagesStatistic: messagesStatistic,
    onUpgradeClick,
    upgradeButtonCaption,
    type: 'MONTHLY_NOTICE',
  };

  return <SendLimitNoticeTemplate {...templateProps} />;
};

const mapStateToProps = (state: StoreState) => ({
  activeMessagesStatistic: activeMessagesStatisticSelector(state),
  activeFacebookPage: activeFacebookPageSelector(state),
  defaultProPlan: defaultProPlanSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openBillingModal: (facebookPage: EnhancedBillingPage, plan: EnhancedPlan) =>
    dispatch(
      modalActions.openBillingModal({
        facebookPage,
        plan,
        subscription: facebookPage.subscription,
      }),
    ),
  openAllSetModal: (message: string) =>
    dispatch(modalActions.openAllSetModal({ message })),
  sendOveragesEnabled: (
    subscriptionId: number,
    overagesEnabled: boolean,
    facebookPageId?: number,
    skipFetchActivePage?: boolean,
  ) =>
    dispatch(
      subscriptionCRUDActions.sendOveragesEnabled(
        subscriptionId,
        overagesEnabled,
        facebookPageId,
        skipFetchActivePage,
      ),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(MonthlySendsWarningModal);
